import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMinistries, createMinistry, updateMinistry, deleteMinistry, archiveMinistry } from '../services/api';
import { formatAccountingCurrency, formatCpfCnpj, validateCpfCnpj } from '../utils/formatters';
import './AdminMinistries.css';

const AdminMinistries = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMinistry, setEditingMinistry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    leaderName: '',
    leaderCpfCnpj: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [removeLogo, setRemoveLogo] = useState(false);
  const [error, setError] = useState('');
  const [cpfCnpjError, setCpfCnpjError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMinistry, setSelectedMinistry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name, balance, leader
  const [viewMode, setViewMode] = useState('cards'); // cards or table

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadMinistries();
  }, [isAdmin, navigate]);

  const loadMinistries = async () => {
    try {
      setLoading(true);
      const response = await getMinistries(new Date().getFullYear());
      setMinistries(response.data);
    } catch (error) {
      console.error('Erro ao carregar ministérios:', error);
      setError('Erro ao carregar ministérios.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (ministry = null) => {
    if (ministry) {
      setEditingMinistry(ministry);
      setFormData({
        name: ministry.name,
        leaderName: ministry.leader.name,
        leaderCpfCnpj: ministry.leader.cpfCnpj
      });
      setLogoPreview(ministry.logoUrl || '');
      setRemoveLogo(false);
    } else {
      setEditingMinistry(null);
      setFormData({
        name: '',
        leaderName: '',
        leaderCpfCnpj: ''
      });
      setLogoPreview('');
      setRemoveLogo(false);
    }
    setLogoFile(null);
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMinistry(null);
    setFormData({
      name: '',
      leaderName: '',
      leaderCpfCnpj: ''
    });
    setLogoFile(null);
    setLogoPreview('');
    setRemoveLogo(false);
    setError('');
    setCpfCnpjError('');
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('A imagem do logo deve ter no máximo 2MB.');
        return;
      }
      setLogoFile(file);
      setRemoveLogo(false);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setRemoveLogo(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateCpfCnpj(formData.leaderCpfCnpj)) {
      setCpfCnpjError('CPF/CNPJ inválido');
      setError('Por favor, corrija os erros no formulário');
      return;
    }
    
    setError('');
    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('leaderName', formData.leaderName);
      submitData.append('leaderCpfCnpj', formData.leaderCpfCnpj);
      
      if (logoFile) {
        submitData.append('logo', logoFile);
      }
      if (removeLogo) {
        submitData.append('removeLogo', 'true');
      }

      if (editingMinistry) {
        await updateMinistry(editingMinistry._id, submitData);
      } else {
        await createMinistry(submitData);
      }
      await loadMinistries();
      handleCloseModal();
    } catch (error) {
      console.error('Erro ao salvar ministério:', error);
      setError(error.response?.data?.message || 'Erro ao salvar ministério.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenArchiveModal = (ministry) => {
    setSelectedMinistry(ministry);
    setShowArchiveModal(true);
  };

  const handleOpenDeleteModal = (ministry) => {
    setSelectedMinistry(ministry);
    setShowDeleteModal(true);
  };

  const handleArchive = async () => {
    if (!selectedMinistry) return;

    try {
      await archiveMinistry(selectedMinistry._id);
      await loadMinistries();
      setShowArchiveModal(false);
      setSelectedMinistry(null);
      alert('Ministério arquivado com sucesso!');
    } catch (error) {
      console.error('Erro ao arquivar ministério:', error);
      alert(error.response?.data?.message || 'Erro ao arquivar ministério.');
    }
  };

  const handleDelete = async () => {
    if (!selectedMinistry) return;

    try {
      const response = await deleteMinistry(selectedMinistry._id);
      await loadMinistries();
      setShowDeleteModal(false);
      setSelectedMinistry(null);
      
      if (response.data.filesDeleted > 0) {
        alert(`Ministério deletado com sucesso!\n${response.data.filesDeleted} arquivo(s) removido(s) do servidor.`);
      } else {
        alert('Ministério deletado com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao deletar ministério:', error);
      alert(error.response?.data?.message || 'Erro ao deletar ministério.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCpfCnpjChange = (e) => {
    const value = e.target.value;
    const formatted = formatCpfCnpj(value);
    setFormData({ ...formData, leaderCpfCnpj: formatted });
    
    if (cpfCnpjError) {
      setCpfCnpjError('');
    }
  };

  const handleCpfCnpjBlur = () => {
    const cleaned = formData.leaderCpfCnpj.replace(/\D/g, '');
    
    if (cleaned.length > 0) {
      if (cleaned.length !== 11 && cleaned.length !== 14) {
        setCpfCnpjError('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos');
      } else if (!validateCpfCnpj(formData.leaderCpfCnpj)) {
        setCpfCnpjError('CPF/CNPJ inválido');
      } else {
        setCpfCnpjError('');
      }
    }
  };

  // Filtrar e ordenar ministérios
  const getFilteredAndSortedMinistries = () => {
    let filtered = [...ministries];

    // Filtrar por busca
    if (searchTerm) {
      filtered = filtered.filter(ministry =>
        ministry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ministry.leader.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'balance':
          return b.currentBalance - a.currentBalance;
        case 'leader':
          return a.leader.name.localeCompare(b.leader.name);
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Calcular estatísticas
  const getStats = () => {
    const totalMinistries = ministries.length;
    const totalBalance = ministries.reduce((sum, m) => sum + m.currentBalance, 0);
    const negativeBalance = ministries.filter(m => m.currentBalance < 0).length;
    const positiveBalance = ministries.filter(m => m.currentBalance > 0).length;

    return { totalMinistries, totalBalance, negativeBalance, positiveBalance };
  };

  const stats = getStats();
  const filteredMinistries = getFilteredAndSortedMinistries();

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1><i className="fas fa-church"></i> Administração de Ministérios</h1>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/admin/archived')} 
              className="btn btn-secondary"
            >
              <i className="fas fa-archive"></i> Arquivo Morto
            </button>
            <button onClick={() => handleOpenModal()} className="btn btn-primary">
              <i className="fas fa-plus"></i> Adicionar Ministério
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon blue">
              <i className="fas fa-church"></i>
            </div>
            <div className="summary-info">
              <span className="summary-label">Total de Ministérios</span>
              <span className="summary-value">{stats.totalMinistries}</span>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon green">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <div className="summary-info">
              <span className="summary-label">Saldo Total</span>
              <span className={`summary-value ${stats.totalBalance < 0 ? 'negative' : 'positive'}`}>
                {formatAccountingCurrency(stats.totalBalance)}
              </span>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon green">
              <i className="fas fa-arrow-up"></i>
            </div>
            <div className="summary-info">
              <span className="summary-label">Saldos Positivos</span>
              <span className="summary-value positive">{stats.positiveBalance}</span>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon red">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="summary-info">
              <span className="summary-label">Saldos Negativos</span>
              <span className="summary-value negative">{stats.negativeBalance}</span>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-bar">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar por nome de ministério ou líder..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-controls">
            <div className="sort-select">
              <i className="fas fa-sort"></i>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">Ordenar por Nome</option>
                <option value="balance">Ordenar por Saldo</option>
                <option value="leader">Ordenar por Líder</option>
              </select>
            </div>

            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
                title="Visualização em Cards"
              >
                <i className="fas fa-th-large"></i>
              </button>
              <button
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Visualização em Tabela"
              >
                <i className="fas fa-table"></i>
              </button>
            </div>
          </div>
        </div>

        {filteredMinistries.length === 0 ? (
          <div className="card empty-state">
            <i className="fas fa-inbox empty-icon"></i>
            <p>{searchTerm ? 'Nenhum ministério encontrado com esse critério.' : 'Nenhum ministério cadastrado.'}</p>
            {!searchTerm && (
              <button onClick={() => handleOpenModal()} className="btn btn-primary">
                <i className="fas fa-plus"></i> Adicionar Primeiro Ministério
              </button>
            )}
          </div>
        ) : viewMode === 'cards' ? (
          <div className="ministries-grid">
            {filteredMinistries.map((ministry) => (
              <div key={ministry._id} className="ministry-card">
                <div className="ministry-card-header">
                  <h3>
                    {ministry.logoUrl ? (
                      <img src={ministry.logoUrl} alt="" className="admin-ministry-logo" />
                    ) : (
                      <i className="fas fa-church"></i>
                    )}
                    {ministry.name}
                  </h3>
                  <span className={`ministry-balance ${ministry.currentBalance < 0 ? 'negative' : 'positive'}`}>
                    {formatAccountingCurrency(ministry.currentBalance)}
                  </span>
                </div>

                <div className="ministry-card-body">
                  <div className="ministry-info-row">
                    <i className="fas fa-user-tie"></i>
                    <span><strong>Líder:</strong> {ministry.leader.name}</span>
                  </div>
                  <div className="ministry-info-row">
                    <i className="fas fa-id-card"></i>
                    <span><strong>CPF/CNPJ:</strong> {ministry.leader.cpfCnpj}</span>
                  </div>
                </div>

                <div className="ministry-card-footer">
                  <button
                    onClick={() => handleOpenModal(ministry)}
                    className="btn btn-sm btn-secondary"
                    title="Editar"
                  >
                    <i className="fas fa-edit"></i> Editar
                  </button>
                  <button
                    onClick={() => handleOpenArchiveModal(ministry)}
                    className="btn btn-sm btn-warning"
                    title="Arquivar"
                  >
                    <i className="fas fa-archive"></i>
                  </button>
                  <button
                    onClick={() => handleOpenDeleteModal(ministry)}
                    className="btn btn-sm btn-danger"
                    title="Deletar"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="ministries-table-container">
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Líder</th>
                    <th className="hide-mobile">CPF/CNPJ</th>
                    <th className="text-right">Saldo Atual</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMinistries.map((ministry) => (
                    <tr key={ministry._id}>
                      <td>
                        <strong>
                          {ministry.logoUrl ? (
                            <img src={ministry.logoUrl} alt="" className="admin-ministry-logo" style={{marginRight: '8px'}} />
                          ) : (
                            <i className="fas fa-church" style={{marginRight: '8px', color: 'var(--primary-blue)'}}></i>
                          )}
                          {ministry.name}
                        </strong>
                      </td>
                      <td><i className="fas fa-user-tie" style={{marginRight: '8px', color: 'var(--gray-500)'}}></i>{ministry.leader.name}</td>
                      <td className="hide-mobile text-muted">{ministry.leader.cpfCnpj}</td>
                      <td className="text-right">
                        <span className={ministry.currentBalance < 0 ? 'text-negative' : 'text-positive'}>
                          {formatAccountingCurrency(ministry.currentBalance)}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleOpenModal(ministry)}
                            className="btn btn-sm btn-secondary"
                            title="Editar"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleOpenArchiveModal(ministry)}
                            className="btn btn-sm btn-warning"
                            title="Arquivar"
                          >
                            <i className="fas fa-archive"></i>
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(ministry)}
                            className="btn btn-sm btn-danger"
                            title="Deletar Permanentemente"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showArchiveModal && selectedMinistry && (
          <div className="modal-overlay" onClick={() => setShowArchiveModal(false)}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2><i className="fas fa-archive"></i> Arquivar Ministério</h2>
                <button className="modal-close" onClick={() => setShowArchiveModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja arquivar o ministério <strong>{selectedMinistry.name}</strong>?</p>
                <div className="alert alert-info">
                  <i className="fas fa-info-circle"></i> O ministério será movido para o arquivo morto mas todos os dados serão mantidos. Você poderá restaurá-lo depois.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowArchiveModal(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleArchive}
                  className="btn btn-warning"
                >
                  <i className="fas fa-archive"></i> Arquivar
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && selectedMinistry && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2><i className="fas fa-trash"></i> Deletar Permanentemente</h2>
                <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja <strong>DELETAR PERMANENTEMENTE</strong> o ministério <strong>{selectedMinistry.name}</strong>?</p>
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle"></i> <strong>Atenção!</strong> Esta ação é irreversível!
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    <li>Todos os reembolsos serão deletados</li>
                    <li>Todos os arquivos (notas fiscais) serão removidos do servidor</li>
                    <li>Todo o histórico será perdido</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-danger"
                >
                  <i className="fas fa-trash"></i> Deletar Permanentemente
                </button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <i className={`fas ${editingMinistry ? 'fa-edit' : 'fa-plus'}`}></i>
                  {editingMinistry ? ' Editar Ministério' : ' Adicionar Ministério'}
                </h2>
                <button className="modal-close" onClick={handleCloseModal}>
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      <i className="fas fa-church"></i> Nome do Ministério *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Ex: Louvor, Jovens, Crianças"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="leaderName" className="form-label">
                      <i className="fas fa-user-tie"></i> Nome do Líder *
                    </label>
                    <input
                      type="text"
                      id="leaderName"
                      name="leaderName"
                      className="form-control"
                      value={formData.leaderName}
                      onChange={handleChange}
                      required
                      placeholder="Nome completo do líder"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="leaderCpfCnpj" className="form-label">
                      <i className="fas fa-id-card"></i> CPF/CNPJ do Líder *
                    </label>
                    <input
                      type="text"
                      id="leaderCpfCnpj"
                      name="leaderCpfCnpj"
                      className={`form-control ${cpfCnpjError ? 'error' : ''}`}
                      value={formData.leaderCpfCnpj}
                      onChange={handleCpfCnpjChange}
                      onBlur={handleCpfCnpjBlur}
                      maxLength="18"
                      required
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    />
                    {cpfCnpjError && (
                      <small className="error-text"><i className="fas fa-exclamation-circle"></i> {cpfCnpjError}</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-image"></i> Logo do Ministério
                      <span className="info-hint" data-tip="Imagem que representa o ministério. Será exibida ao lado do nome nos cards e páginas. Formatos: PNG, JPG, WEBP ou SVG. Máx: 2MB.">
                        <i className="fas fa-info-circle"></i>
                      </span>
                    </label>
                    
                    {logoPreview ? (
                      <div className="logo-preview-container">
                        <img src={logoPreview} alt="Preview do logo" className="logo-preview-img" />
                        <div className="logo-preview-actions">
                          <label className="btn btn-sm btn-secondary logo-change-btn">
                            <i className="fas fa-exchange-alt"></i> Trocar
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                              onChange={handleLogoChange}
                              style={{ display: 'none' }}
                            />
                          </label>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={handleRemoveLogo}
                          >
                            <i className="fas fa-trash"></i> Remover
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="logo-upload-area">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                          onChange={handleLogoChange}
                          style={{ display: 'none' }}
                        />
                        <div className="logo-upload-content">
                          <i className="fas fa-cloud-upload-alt"></i>
                          <span>Clique para selecionar uma imagem</span>
                          <small>PNG, JPG, WEBP ou SVG • Máx. 2MB</small>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-secondary"
                    disabled={submitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    <i className="fas fa-save"></i> {submitting ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMinistries;
